import {
  ZipReader,
  BlobReader,
  BlobWriter,
  TextWriter,
  type FileEntry,
  type DirectoryEntry
} from '@zip.js/zip.js'
import * as indexedDB from '@/accessors/indexedDB'
import type { JsonData, UpdateData } from './interface'
import photoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import { getZipPassword } from './passwordGenerator'
import { isDateTimeString } from '@/utils/dateUtil/dateUtils'
import type { SendState } from '@/types/accessor/indexedDB'

/**
 * indexedDBからアップロードするためのデータを取得する
 * @returns アップロードするデータ
 */
export const getUpdateData = async (): Promise<UpdateData[] | null> => {
  // 調査結果を取得
  const inspectionResultList = await indexedDB.inspectionResult.getAllFromIndex({
    indexName: 'method',
    indexState: 'update'
  })
  if (!inspectionResultList || inspectionResultList.length === 0) return null

  // 取得した調査結果からPhotoDBを取得し、UpdateDataに変換
  const getUpdateDataTasks: Array<Promise<UpdateData>> = inspectionResultList.map(async r => {
    const sendState = r.send_state
    const inspectionResult = r.data
    const photoStates = await photoDBOperator.queryPrefixMatchItems(
      [inspectionResult.govCode, inspectionResult.disasterId, inspectionResult.assessmentId].join(
        '/'
      )
    )
    const returnData: UpdateData = {
      sendState,
      inspectionResult,
      photoStates
    }
    return returnData
  })

  return await Promise.all(getUpdateDataTasks)
}

/**
 * zipファイルリストからアップロードするデータを読み込む
 * @param zipFileList zipファイルリスト
 * @returns アップロードするデータ
 */
export const readZipFileListData = async (zipFileList: FileList): Promise<JsonData[]> => {
  const uploadDataList: JsonData[] = []

  for (const zipFile of zipFileList) {
    if (zipFile == null) continue
    const zipFileData = await readZipFileData(zipFile)
    uploadDataList.push(...zipFileData)
  }

  return uploadDataList
}

/**
 * zipファイルからアップロードするデータを読み込む
 * @param zipFile zipファイル
 * @returns アップロードするデータ
 */
const readZipFileData = async (zipFile: File): Promise<JsonData[]> => {
  const entries = await new ZipReader(new BlobReader(zipFile)).getEntries()
  // 応急システムによって生成されたデータかどうか確認する
  if (
    !entries.some(
      entry => isDateTimeString(entry.filename) && zipFile.name.includes(entry.filename)
    ) &&
    entries.some(e => (e as DirectoryEntry) !== null)
  ) {
    throw new Error('応急システムで作成されたデータではありません')
  }

  const fileEntries = entries as FileEntry[]
  const password = await getZipPassword()
  const jsonList = await readJsonData(fileEntries, password)

  // 画像読込
  const photoEntries = fileEntries.filter(entry => !entry.filename.endsWith('json'))
  photoEntries.forEach(async fileEntry => {
    if (fileEntry.getData === undefined) return
    const photoFileName = fileEntry.filename
    for (const json of jsonList) {
      for (const photoState of json.photoStates) {
        if (!photoFileName.includes(photoState.fileName)) return
        const photoBlob = await fileEntry.getData(new BlobWriter(), { password })
        photoState.arrayBuffer = await photoBlob.arrayBuffer()
        break
      }
    }
  })
  return jsonList
}

/**
 * zipファイルのentryから、調査データのjsonファイルを読み込む
 * @param entries zipファイルのentry
 * @param password zipファイル生成時にかけたpassword
 * @returns zipファイルから読みだしたjsonデータ（オブジェクト化してある）
 */
const readJsonData = async (entries: FileEntry[], password: string): Promise<UpdateData[]> => {
  const jsonEntries = entries.filter(entry => entry.filename.endsWith('json'))
  const jsonStringList = await Promise.all(
    jsonEntries.map(async jsonEntry => {
      if (jsonEntry.getData === undefined) return undefined
      return await jsonEntry.getData(new TextWriter(), { password })
    })
  )
  // filter(v => v !== undefined)だと型情報からundefinedが抜けないため型ガードで実装
  const jsonList = jsonStringList
    .filter((jsonString): jsonString is string => typeof jsonString === 'string')
    .map(jsonString => JSON.parse(jsonString)) as UpdateData[]
  return jsonList
}

/**
 * 中断調査のsendStateをedited → featchedに更新する
 * @param sendState 変更後sendState
 * @returns なし
 */
export const updateInterruptedInspectionSendStates = async (
  sendState: SendState
): Promise<void> => {
  const updateData = await getUpdateData()
  // 中断調査にフィルター
  if (updateData == null) return
  const interruptedUpdateDataList = updateData.filter(
    (getUpdateData: UpdateData) => getUpdateData.sendState === 'edited'
  )
  for (const interruptedUpdateData of interruptedUpdateDataList) {
    await indexedDB.inspectionResult.updateSendStates({
      data: interruptedUpdateData.inspectionResult,
      sendState: sendState
    })
  }
}
